import type { ReactNode } from 'react';
import { toShortTime } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { PublicDayAvailability } from '../../core/models/public-day-availability';
import { BookingCalendar } from './booking-calendar';
import { ClockIcon } from './booking-icons';
import { groupSlots } from './slot-groups';

interface SlotStepProps {
    page: number;
    pages: number;
    days: ReadonlyMap<string, PublicDayAvailability>;
    /** Null until the availability window has arrived, which is what there is to draw. */
    today: string | null;
    loading: boolean;
    /**
     * Whether the customer named a consultant, which is whose diary this calendar is. It changes
     * only what an empty window says: with nobody named, no days at all means the company has
     * nothing open; with somebody named it means *they* have nothing open, and the way out is the
     * step before this one.
     */
    specialistChosen: boolean;
    date: string | null;
    /** The wire value, `"HH:mm:ss"`, exactly as the slots route returned it. */
    time: string | null;
    /**
     * Times the server has since refused, so a lost race is visible on the slot rather than as a
     * banner. Wire values, like `time` itself.
     */
    takenTimes: readonly string[];
    errors: Record<string, TranslationKey>;
    onPageChange: (page: number) => void;
    onDateChange: (date: string) => void;
    onTimeChange: (time: string) => void;
    /** The wizard's error banner and button row, which the live page draws inside the card. */
    children: ReactNode;
}

/**
 * Step three: the day and the time. Skipped entirely for an online request, which picks no time at
 * all — staff call back.
 *
 * **The times are one consultant's when the customer named one**, because the request will carry
 * that name: a time drawn from the union of everybody's diaries is not a time that person is free
 * at. The server decides which calendar this is; the only thing the step does about it is explain
 * an empty one.
 *
 * **Every calendar rule belongs to the server now.** The live grid disables Sunday with
 * `getDay() === 0` and caps at `today + 30`, both hardcoded in the browser; here each day carries
 * its own reason, so a closed Sunday, a booked-out Thursday and a public holiday are all simply
 * days with no times on them — and the reason for the one the customer actually clicked is spelled
 * out below the grid instead of left to a greyed-out square.
 */
export function SlotStep({
    page,
    pages,
    days,
    today,
    loading,
    specialistChosen,
    date,
    time,
    takenTimes,
    errors,
    onPageChange,
    onDateChange,
    onTimeChange,
    children
}: SlotStepProps) {
    const { t } = useTranslation();
    const selectedDay = date === null ? undefined : days.get(date);

    // The window arrives as every day with its reason, so a consultant with no schedule is thirty
    // closed days rather than none — a grid with nothing to click and nothing said about why.
    const nothingFree = ![...days.values()].some((day) => day.slots.length > 0);

    return (
        <div className="card">
            <span className="card-lbl">{t('booking.s2.step')}</span>
            <h1 className="card-title">{t('booking.s2.title')}</h1>

            <div className="dt-grid">
                {today === null || (nothingFree && !loading) ? (
                    loading ? (
                        <p className="tp-loading">
                            <span className="spinner" aria-hidden="true" />
                            {t('booking.loading_slots')}
                        </p>
                    ) : (
                        <p className="no-slots">
                            {t(specialistChosen ? 'booking.s2.none_for_specialist' : 'booking.no_days')}
                        </p>
                    )
                ) : (
                    <BookingCalendar
                        page={page}
                        pages={pages}
                        days={days}
                        today={today}
                        selected={date}
                        onPageChange={onPageChange}
                        onSelect={onDateChange}
                    />
                )}

                <div data-reveal="times">
                    <h2 className="tp-head">{t('booking.step.time')}</h2>

                    {loading ? (
                        <p className="tp-loading">
                            <span className="spinner" aria-hidden="true" />
                            {t('booking.loading_slots')}
                        </p>
                    ) : date === null ? (
                        <p className="tp-empty">
                            <ClockIcon />
                            <span>{t('booking.s2.pick_date')}</span>
                        </p>
                    ) : selectedDay === undefined || selectedDay.slots.length === 0 ? (
                        <p className="no-slots">{t(reasonKey(selectedDay?.availability))}</p>
                    ) : (
                        <div className="t-scroll">
                            {groupSlots(selectedDay.slots).map((group) => (
                                <div key={group.label}>
                                    <h3 className="tg-lbl">{t(group.label)}</h3>
                                    <div className="t-chips">
                                        {group.slots.map((slot) => {
                                            const taken = takenTimes.includes(slot);
                                            const classes = ['t-chip'];

                                            if (taken) classes.push('bkd');
                                            if (slot === time) classes.push('sel');

                                            return (
                                                <button
                                                    key={slot}
                                                    type="button"
                                                    className={classes.join(' ')}
                                                    disabled={taken}
                                                    aria-pressed={slot === time}
                                                    // A struck-through chip says "taken" in styling
                                                    // alone, which is what a screen reader loses.
                                                    aria-label={
                                                        taken
                                                            ? `${toShortTime(slot)} — ${t('booking.slot_taken')}`
                                                            : undefined
                                                    }
                                                    onClick={() => onTimeChange(slot)}
                                                >
                                                    {toShortTime(slot)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {errors['preferredDate'] !== undefined && (
                <p className="fe" role="alert">
                    {t(errors['preferredDate'])}
                </p>
            )}

            {errors['preferredTime'] !== undefined && (
                <p className="fe" role="alert">
                    {t(errors['preferredTime'])}
                </p>
            )}

            {children}
        </div>
    );
}

/** Why a day has no times. Three answers where the live form has one. */
function reasonKey(availability: string | undefined): TranslationKey {
    switch (availability) {
        case 'Closed':
            return 'booking.day.closed';
        case 'FullyBooked':
            return 'booking.day.fully_booked';
        default:
            return 'booking.day.unavailable';
    }
}
